import { useEffect, useRef } from 'react';
import { clsx } from 'clsx';
import { AuditHistoryPanel } from '../../../audit';
import type { AuditHistoryProps } from '../../../audit/types/audit.types';
import { Button } from '../../Button/Button';
import { XIcon } from '../icons';
import { useMasterDocumentTranslation } from '../i18n';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export interface MasterDocumentAuditModalProps {
  isOpen: boolean;
  title: string;
  onClose: () => void;
  /** ID de la entidad/documento cuyo historial se muestra. */
  entityId: string;
  /** Función de fetch del historial (del `auditHistory` del provider). */
  fetchFn: AuditHistoryProps['fetchFn'];
}

/**
 * Marco del modal de historial de auditoría, propiedad del kit. Replica el chrome
 * y la accesibilidad del `MasterDocumentPrintModal` (overlay, cierre con Esc, focus
 * trap y restauración de foco) y monta en el cuerpo el `AuditHistoryPanel` compartido
 * de `src/audit`. No hay lógica de fetch aquí: la aporta el host vía `fetchFn`.
 */
export function MasterDocumentAuditModal({
  isOpen,
  title,
  onClose,
  entityId,
  fetchFn,
}: MasterDocumentAuditModalProps) {
  const { t } = useMasterDocumentTranslation();
  const dialogRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    triggerRef.current = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !dialogRef.current) return;
      const focusables = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      ).filter((el) => el.offsetParent !== null);
      if (focusables.length === 0) {
        e.preventDefault();
        dialogRef.current.focus();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || active === dialogRef.current)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      triggerRef.current?.focus?.();
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        className={clsx(
          'flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-lg shadow-xl outline-none',
          'bg-white dark:bg-dark-bg-primary',
        )}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        {/* Cabecera — mismo estilo que el modal de creación/edición de MasterCrud
            (título en color de marca + botón de cierre outline con icono). */}
        <div className="flex flex-none items-center justify-between gap-4 px-5 pb-3 pt-5">
          <h2 className="truncate text-[16px] font-medium text-primary-600 dark:text-primary-500">
            {title}
          </h2>
          <Button
            type="outline"
            size="xs"
            iconOnly
            leftIcon={<XIcon className="h-4 w-4" />}
            onClick={onClose}
            ariaLabel={t('master_document.chrome.close')}
            className="flex !bg-white dark:!bg-dark-bg-primary"
          />
        </div>

        {/* Cuerpo — panel de historial compartido */}
        <div className="min-h-0 flex-1 overflow-hidden">
          <AuditHistoryPanel fetchFn={fetchFn} entityId={entityId} />
        </div>

        {/* Footer — mismo botón de cierre y remate que el footer de MasterCrud. */}
        <div className="relative z-[200] flex flex-none items-center justify-end gap-4 border-t border-border-primary/50 bg-white px-5 pb-5 pt-3 shadow-sm dark:border-dark-border-primary/30 dark:bg-dark-bg-primary">
          <Button
            type="outline"
            size="l"
            onClick={onClose}
            className="px-6 font-medium hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            {t('master_document.chrome.close')}
          </Button>
        </div>
      </div>
    </div>
  );
}
